import "./App.css";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "./styles/theme";
import Layout from "./layout/Layout";
import Router from "./router/Router";
import ApiInterceptor from "./hooks/ApiInterceptor";
import { BrowserRouter } from "react-router-dom";
import { SnackBarProvider } from "./providers/SnackBarProvider";
import { UserProvider } from "./users/providers/UserProvider";
import { CurrencyProvider } from "./providers/CurrencyProvider";
import { FriendsProvider } from "./users/friends/friendsProvider/FriendsProvider.jsx";
import { CollectionProvider } from "./cards/collectionProvider/CollectionProvider";
import { WishlistProvider } from "./wishlist/providers/WishlistProvider";
import { NotificationProvider } from "./notifications/providers/NotificationProvider";
import { TradeActionProvider } from "./trades/providers/TradeActionProvider";
import { FeedbackProvider } from "./feedback/FeedbackProvider";
import FirstRunConsentGate from "./legal/FirstRunConsentGate";
import RuntimeFlagsLoader from "./utils/RuntimeFlagsLoader";
import ScrollbarStyles from "./styles/styles";
import ErrorBoundary from "./layout/components/ErrorBoundary";
import useKeyboardBridge from "./native/keyboardBridge";
import SplashGate from "./native/splashScreen";

/**
 * The two native bridges that have to outlive any single screen.
 *
 * The keyboard listener is mounted here rather than on the chat route because
 * the IME can open anywhere, and a listener scoped to one route would leave a
 * stale inset behind on navigation. SplashGate sits inside UserProvider — it
 * needs `authReady`, which App itself cannot read because App is what renders
 * the provider.
 */
const NativeShell = () => {
  useKeyboardBridge();
  return null;
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className="App">
          <ScrollbarStyles />
          <NativeShell />
          <BrowserRouter>
          <UserProvider>
            <SplashGate />
            <CurrencyProvider>
            <NotificationProvider>
              <TradeActionProvider>
                <SnackBarProvider>
                  {/* Above the router: the interceptor's lifetime must not
                      depend on which route is showing. */}
                  <ApiInterceptor />
                  {/* Learns which flags the SERVER has in force. Below the
                      interceptor so the request carries the auth header. */}
                  <RuntimeFlagsLoader />
                  {/* #65: an OAuth account arrives with no acceptance on file. Mounted
                      route-independently so the gate covers every screen, and below the
                      interceptor so its request carries the auth header. It also shows
                      Q-20's "We've updated our Terms" screen (PolicyUpdateGate) to an
                      account that accepted an older version. */}
                  <FirstRunConsentGate />
                  <FeedbackProvider>
                    <FriendsProvider>
                      <CollectionProvider>
                        <WishlistProvider>
                          <Layout>
                            <Router />
                          </Layout>
                        </WishlistProvider>
                      </CollectionProvider>
                    </FriendsProvider>
                  </FeedbackProvider>
                </SnackBarProvider>
              </TradeActionProvider>
            </NotificationProvider>
            </CurrencyProvider>
          </UserProvider>
          </BrowserRouter>
        </div>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
